import Modal from 'components/Modal';
import styles from './PayModal.module.scss';
import Typography from 'components/Typography';
import ReactToPrint from 'react-to-print';
import classNames from 'classnames';
import Button from 'components/Button';
import QRCode from 'react-qr-code';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  DataTableSchemaItem,
  OfferingType,
  Prescription,
  RequestDetails,
  ScriptStatus,
} from 'types';
import CheckIcon from 'pages/Requests/CheckIcon';
import Select from 'components/Select';
import DataTable from 'components/DataTable';
import {get} from 'lodash';
import {OFFERING_TYPE_OPTIONS_LABELS} from 'constants/select-options';
import {formatPrice} from 'utils/format-price';

type Props = {
  data?: RequestDetails;
  onClose: () => void;
  isOpen: boolean;
  paymentUrl: string;
  handleSendNotification: (types: any, items: NotifyItem[]) => void;
};

type NotifyItem = {
  id: number;
  amount_due: number;
  status: ScriptStatus;
  name: string;
};
const getNotifyItems = (prescriptions: Prescription[]) => {
  return (
    prescriptions?.reduce((pr, curr: Prescription) => {
      if (Number(curr.amount_due) > 0) {
        return [
          ...pr,
          {
            id: curr.id,
            amount_due: Number(curr.amount_due) || 0,
            status: curr.status,
            name: curr.name,
          },
        ];
      }
      return pr;
    }, [] as NotifyItem[]) || []
  );
};
export default function PayModal({
  data,
  onClose,
  isOpen,
  paymentUrl,
  handleSendNotification,
}: Props) {
  const printRef = useRef<HTMLDivElement>(null);
  const [notifyToPay, setNotifyToPay] = useState({
    is_sms: true,
    is_email: false,
    is_app_push: false,
  });
  const [notifyItems, setNotifyItems] = useState<NotifyItem[]>([]);
  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    if (data?.prescriptions) {
      const items: NotifyItem[] = getNotifyItems(data?.prescriptions);
      setNotifyItems(items);
    }
  }, [data?.prescriptions]);

  const orderItems = useMemo(() => {
    return data?.prescriptions?.map((item) => ({
      checkbox: (
        <div
          onClick={(val) => {
            val.stopPropagation();
            const include = notifyItems.find((i) => i.id === item.id);

            if (include) {
              setNotifyItems(notifyItems.filter((i) => i.id !== item.id));
            } else {
              setNotifyItems([
                ...notifyItems,
                {
                  id: item.id,
                  name: item.name,
                  amount_due: Number(item.amount_due) || 0,
                  status: item.status,
                },
              ]);
            }
          }}
        >
          <CheckIcon
            emptySelect={!notifyItems.find((i) => i.id === item.id)}
            allSelected={!!notifyItems.find((i) => i.id === item.id)}
          />
        </div>
      ),
      id: item.id,
      name: item.name,
      quantity: item.quantity,
      price: item.amount_due,
      type:
        item?.offering_type &&
        OFFERING_TYPE_OPTIONS_LABELS[item?.offering_type as OfferingType],
    }));
  }, [data?.prescriptions, notifyItems]);

  const NOTIFY_ITEMS_SCHEMA: DataTableSchemaItem[] = useMemo(
    () => [
      {
        dataKey: 'checkbox',
        header:
          notifyItems.length > 0 ? (
            <div
              onClick={(val) => {
                val.stopPropagation();
              }}
              className={styles.selectAll}
            >
              <div
                onClick={(val) => {
                  setAllSelected(false);
                  setNotifyItems([]);
                }}
                className={styles.selectAllIcon}
              >
                <CheckIcon emptySelect={false} allSelected={allSelected} />
              </div>
              <Select
                components={{
                  Input: () => null,
                  ValueContainer: () => null,
                }}
                styles={{
                  control: (base) => ({
                    ...base,
                    border: 'none',
                    backgroundColor: 'transparent',
                  }),
                  container: (base) => ({
                    ...base,
                    width: 70,
                  }),
                  option: (base) => ({
                    ...base,
                    paddingTop: 3,
                    paddingBottom: 3,
                    minHeight: 'auto !important',
                  }),
                  menuList: (base) => ({
                    ...base,
                    gap: 0,
                    padding: 0,
                  }),
                }}
                options={[
                  {label: 'All', value: 'all'},
                  {label: 'None', value: 'none'},
                ]}
                onChange={(val) => {
                  if (val?.value === 'all') {
                    const items: NotifyItem[] = getNotifyItems(
                      data?.prescriptions || []
                    );
                    setNotifyItems(items);
                    setAllSelected(true);
                  } else if (val?.value === 'none') {
                    setNotifyItems([]);
                    setAllSelected(false);
                  }
                }}
              />
            </div>
          ) : (
            <div style={{height: 40}}></div>
          ),
        sortable: false,
        colWidth: '60px',
      },
      {
        dataKey: 'name',
        header: 'Item Name',
        sortable: false,
        colWidth: '200px',
      },
      {
        dataKey: 'quantity',
        header: 'Quantity',
        colWidth: '80px',
      },
      {
        dataKey: 'type',
        header: 'Offering',
        colWidth: '90px',
      },
      {
        dataKey: 'price',
        header: 'Amount Due',
        colWidth: '60px',
      },
    ],
    [allSelected, data?.prescriptions, notifyItems]
  );
  const notifyToPayActions = useMemo(() => {
    const types = {
      is_sms: 'SMS',
      is_email: 'Email',
      is_app_push: 'App Push',
    };
    return Object.keys(notifyToPay).map((key) => {
      return (
        <div className={styles.notify_to_pay_action} key={key}>
          <input
            type="checkbox"
            className={styles.notify_to_pay_action__checkbox}
            checked={notifyToPay[key as keyof typeof notifyToPay]}
            onChange={() =>
              setNotifyToPay({
                ...notifyToPay,
                [key]: !notifyToPay[key as keyof typeof notifyToPay],
              })
            }
          />
          <Typography variant="h3">
            {types[key as keyof typeof types]}
          </Typography>
        </div>
      );
    });
  }, [notifyToPay]);
  return (
    <Modal
      onClose={onClose}
      className={styles.notify_to_pay_modal}
      isOpen={isOpen}
    >
      <Typography variant="h1">Notify to Pay</Typography>
      <div className={styles.notify_to_pay_actions}>{notifyToPayActions}</div>
      <div>
        <Typography
          style={{marginBottom: 20}}
          className={styles.title}
          variant="h2"
        >
          Payment Details
        </Typography>

        <div ref={printRef} className={styles.print_content}>
          <InfoRow>
            <Label text="Patient" />
            <Value>
              {get(data, 'customer.user.first_name', '') +
                ' ' +
                get(data, 'customer.user.last_name', '')}
            </Value>
          </InfoRow>

          <InfoRow>
            <Label text="Order #" />
            <Value>{get(data, 'sequential_order_number')}</Value>
          </InfoRow>

          <InfoRow>
            <Label text="Status" />
            <Typography
              className={classNames(styles.statusLabel, styles.unpaid)}
              variant="p"
            >
              Unpaid
            </Typography>
          </InfoRow>
          <div style={{margin: '10px 0'}}>
            {notifyItems?.map((item) => (
              <InfoRow>
                <Label text={item.name} />
                <div className={styles.dashed_line} />
                <Value>${item.amount_due}</Value>
              </InfoRow>
            ))}
          </div>
          <div className={styles.line_break} />
          <InfoRow>
            <Typography variant="h3" className={styles.total}>
              Total
            </Typography>
            <Typography variant="h3" className={styles.total}>
              {formatPrice(
                notifyItems?.reduce(
                  (pr, curr) => pr + Number(curr?.amount_due),
                  0
                )
              )}
            </Typography>
          </InfoRow>
          <Typography className={styles.m20} variant="h3" textAlign="center">
            Scan to pay:
          </Typography>
          {paymentUrl && (
            <div className={styles.qr_code}>
              <QRCode size={100} value={paymentUrl} />
            </div>
          )}
        </div>
        <ReactToPrint
          pageStyle={'@page { size: 3.5in 6in; margin: 10mm; }'}
          trigger={() => (
            <Typography className={styles.download_invoice} variant="p">
              Download Invoice
            </Typography>
          )}
          content={() => printRef.current}
        />
      </div>
      <DataTable data={orderItems || []} schema={NOTIFY_ITEMS_SCHEMA} />
      <Button
        style={{
          marginLeft: 'auto',
          marginTop: 20,
        }}
        onClick={() => {
          handleSendNotification(notifyToPay, notifyItems);
          onClose();
        }}
        disabled={notifyItems?.length === 0}
      >
        Send
      </Button>
    </Modal>
  );
}
const Label = ({text}: {text: string}) => {
  return <Typography variant="label">{text}</Typography>;
};

const Value = ({children}: {children: React.ReactNode}) => {
  return <Typography variant="p">{children}</Typography>;
};

const InfoRow = ({children}: {children: React.ReactNode}) => {
  return <div className={classNames(styles.infoRow)}>{children}</div>;
};
